/**
 * Handoff liviano entre la pantalla SceneDesigns (picker) y SceneInitializer.
 *
 * El picker corre ANTES del dashboard, donde aún no hay cámaras/devices listos
 * para armar la escena viva. Así que solo registra los SLUGS del diseño elegido
 * (string chico, no el IStudio completo que puede pesar MB por los fondos en
 * data-URL). SceneInitializer los consume en el primer arranque: baja los
 * templates del backend, arma las variantes y las instala junto a la escena
 * viva del adapter. Si no hay nada elegido (Omitir / backend caído), cae al
 * seed local.
 */

const KEY = "@Eriol/system/pendingSceneDesignSlugs";

/**
 * Marca que el usuario pasó por el picker y eligió NO usar ningún diseño
 * ("Omitir"). Es distinto de "no hay nada pendiente": sin esta marca,
 * SceneInitializer no puede distinguir "omitió" de "nunca vio el picker" y
 * sembraba igual las 3 escenas gráficas locales (Inicio/Pausa/Cierre) — el
 * usuario decía que no quería diseños y se encontraba con 4 escenas, 3 de
 * ellas vacías.
 */
const SKIPPED_KEY = "@Eriol/system/sceneDesignSkipped";

export const setPendingSceneDesign = (slugs: string[]): void => {
  try {
    localStorage.setItem(KEY, JSON.stringify(slugs));
    localStorage.removeItem(SKIPPED_KEY);
  } catch {
    /* localStorage no disponible — el picker simplemente no preselecciona */
  }
};

/** El usuario eligió "Omitir" en el picker: arranca sin escenas de diseño. */
export const setSceneDesignSkipped = (): void => {
  try {
    localStorage.setItem(SKIPPED_KEY, "1");
  } catch {
    /* noop */
  }
};

/** Lee la marca de "Omitir" SIN borrarla. */
export const readSceneDesignSkipped = (): boolean => {
  try {
    return localStorage.getItem(SKIPPED_KEY) === "1";
  } catch {
    return false;
  }
};

/** Lee y BORRA la marca de "Omitir" (consumo único, en el primer arranque). */
export const consumeSceneDesignSkipped = (): boolean => {
  const skipped = readSceneDesignSkipped();
  if (skipped) {
    try {
      localStorage.removeItem(SKIPPED_KEY);
    } catch {
      /* noop */
    }
  }
  return skipped;
};

/** Lee los slugs pendientes SIN borrarlos. */
export const readPendingSceneDesign = (): string[] => {
  try {
    const raw = localStorage.getItem(KEY);
    const parsed = raw ? JSON.parse(raw) : [];
    return Array.isArray(parsed)
      ? parsed.filter((s): s is string => typeof s === "string")
      : [];
  } catch {
    return [];
  }
};

/**
 * Lee y BORRA los slugs pendientes (consumo único).
 *
 * Preferí `readPendingSceneDesign` + `clearPendingSceneDesign` cuando el consumo
 * depende de una operación async que puede fallar (p.ej. bajar templates del
 * backend): si borrás antes y el backend está caído, perdés la elección del
 * usuario sin posibilidad de reintento.
 */
export const consumePendingSceneDesign = (): string[] => {
  const slugs = readPendingSceneDesign();
  if (slugs.length > 0) clearPendingSceneDesign();
  return slugs;
};

export const clearPendingSceneDesign = (): void => {
  try {
    localStorage.removeItem(KEY);
  } catch {
    /* noop */
  }
};
